import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { priceList } from '@/mocks/priceData';
import { newsList } from '@/mocks/newsData';
import type { PriceItem } from '@/types';
import { formatPercent, formatPrice, formatSigned } from '@/utils/format';
import PriceChart, { type ChartView } from './components/PriceChart';

const viewTabs: { key: ChartView; label: string }[] = [
  { key: 'day', label: '日' },
  { key: 'week', label: '周' },
  { key: 'month', label: '月' },
];

export default function PriceDetail() {
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<ChartView>('day');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => setLoading(false), 350);
    return () => clearTimeout(timer);
  }, [id]);

  const item = (priceList as PriceItem[]).find((entry) => entry.id === id);

  const relatedNews = newsList.slice(0, 4);

  const handleShare = () => {
    const url = window.location.href;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url).catch(() => undefined);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <Layout>
        <div className="mx-auto max-w-portal px-4 py-16 md:px-6">
          <div className="flex flex-col items-center justify-center text-center">
            <span className="flex h-12 w-12 animate-pulse items-center justify-center rounded-full bg-background-100 text-xl text-foreground-400">
              <i className="ri-loader-4-line animate-spin" aria-hidden="true"></i>
            </span>
            <p className="mt-4 text-sm text-foreground-500">正在加载行情数据…</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!item) {
    return (
      <Layout>
        <div className="mx-auto max-w-portal px-4 py-16 md:px-6">
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-2xl text-foreground-400">
              <i className="ri-error-warning-line" aria-hidden="true"></i>
            </span>
            <p className="mt-4 text-base font-medium text-foreground-800">未找到该行情数据</p>
            <p className="mt-1 text-sm text-foreground-500">数据可能已下线，请返回行情列表重新选择。</p>
            <Link
              to="/category/gold-price"
              className="mt-5 rounded-md bg-primary-500 px-5 py-2 text-sm font-semibold text-foreground-950 transition-colors hover:bg-primary-400 whitespace-nowrap"
            >
              返回黄金行情
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const up = item.change >= 0;
  const stats = [
    { label: '开盘价', value: formatPrice(item.open) },
    { label: '最高价', value: formatPrice(item.high) },
    { label: '最低价', value: formatPrice(item.low) },
    { label: '昨收价', value: formatPrice(item.prevClose) },
    { label: '成交量', value: item.volume },
    { label: '计价单位', value: item.unit },
  ];

  return (
    <Layout>
      <title>{`${item.name} - ${formatPrice(item.price)} 元 - 黄金`}</title>
      <meta name="description" content={`${item.name}（${item.symbol}）最新价格 ${formatPrice(item.price)} ${item.unit}，${up ? '上涨' : '下跌'}${formatPercent(item.changePercent)}，包含开高低收、成交量等详细行情数据与走势分析。`} />
      <meta name="keywords" content={`${item.name},${item.symbol},黄金价格,黄金行情,${item.market}`} />

      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-portal px-4 py-6 md:px-6">
          <div className="mt-1">
            <Breadcrumb
              items={[{ label: '首页', to: '/' }, { label: '黄金行情', to: '/category/gold-price' }, { label: item.name }]}
            />
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-portal px-4 py-8 md:px-6 md:py-10">
        <div className="grid gap-8 lg:grid-cols-[1fr_320px]">
          <div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <h1 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">{item.name}</h1>
                    <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs font-medium text-secondary-900">
                      {item.type === 'spot' ? '现货' : '期货'}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-foreground-500">
                    {item.symbol} · {item.market}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleShare}
                  className="inline-flex items-center gap-1.5 self-start rounded-md border border-background-300 px-3 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700 whitespace-nowrap cursor-pointer"
                >
                  <i className={copied ? 'ri-check-line text-accent-600' : 'ri-share-line'} aria-hidden="true"></i>
                  {copied ? '链接已复制' : '分享'}
                </button>
              </div>

              <div className="mt-5 flex flex-wrap items-end gap-4">
                <div className={`text-4xl font-bold tabular-nums ${up ? 'text-accent-600' : 'text-secondary-500'}`}>
                  {formatPrice(item.price)}
                </div>
                <div className="text-sm text-foreground-500">{item.unit}</div>
                <div
                  className={`flex items-center gap-1 rounded-md px-2.5 py-1 text-sm font-semibold tabular-nums ${
                    up ? 'bg-accent-100 text-accent-800' : 'bg-secondary-100 text-secondary-600'
                  }`}
                >
                  <i className={up ? 'ri-arrow-up-line' : 'ri-arrow-down-line'} aria-hidden="true"></i>
                  {formatSigned(item.change)} ({formatPercent(item.changePercent)})
                </div>
              </div>

              <div className="mt-6 grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-background-200 bg-background-200 sm:grid-cols-3">
                {stats.map((stat) => (
                  <div key={stat.label} className="bg-background-50 p-4">
                    <div className="text-xs text-foreground-500">{stat.label}</div>
                    <div className="mt-1 text-sm font-semibold tabular-nums text-foreground-950">{stat.value}</div>
                  </div>
                ))}
              </div>

              <div className="mt-3 flex items-center gap-1.5 text-xs text-foreground-400">
                <i className="ri-time-line" aria-hidden="true"></i>
                数据更新时间：{item.updatedAt}
              </div>
            </div>

            <div className="mt-5 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
                  <i className="ri-line-chart-line text-primary-600" aria-hidden="true"></i>
                  价格走势
                  <span className="rounded-full bg-background-200 px-2 py-0.5 text-xs font-normal text-foreground-500">
                    演示数据
                  </span>
                </h2>
                <div className="flex rounded-full border border-background-300 p-1">
                  {viewTabs.map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      onClick={() => setView(tab.key)}
                      className={`rounded-full px-5 py-1.5 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                        view === tab.key ? 'bg-primary-500 text-foreground-950' : 'text-foreground-600 hover:text-primary-700'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="mt-4 overflow-x-auto">
                <div className="min-w-[520px]">
                  <PriceChart item={item} view={view} />
                </div>
              </div>
            </div>
          </div>

          <aside className="lg:sticky lg:top-28 lg:self-start">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
                <i className="ri-link-m" aria-hidden="true"></i>
                相关资讯
              </h2>
              <ul className="mt-3 divide-y divide-background-200">
                {relatedNews.map((news) => (
                  <li key={news.id} className="py-3 first:pt-0 last:pb-0">
                    <Link to={`/detail/news/${news.id}`} className="group flex gap-2">
                      <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-primary-500"></span>
                      <span className="min-w-0">
                        <span className="line-clamp-2 text-sm leading-snug text-foreground-800 transition-colors group-hover:text-primary-700">
                          {news.title}
                        </span>
                        <span className="mt-1 block text-xs text-foreground-400">{news.date}</span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </Layout>
  );
}